import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useRazorpay } from 'react-razorpay';
import { Loader2, Timer } from 'lucide-react';
import toast from 'react-hot-toast';
import { normalCourtPayment, partialBookingCourtPayment, splitPaymentCourtPayment } from '../Utils/API.jsx';
import { RazorPayKey } from '../Assets/Urls/Urls.jsx';
import { images } from '../Utils/Images';

const PaymentProcessing = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { Razorpay } = useRazorpay();
    const [paymentInitiated, setPaymentInitiated] = useState(false);

    const {
        amount,
        bookingId,
        bookingData,
        successData,
        paymentType, // 'normal', 'split', 'partial'
        orderState
    } = location.state || {};

    useEffect(() => {
        // Prevent user from navigating away during payment
        const handleBeforeUnload = (e) => {
            e.preventDefault();
            e.returnValue = 'Please complete the payment process';
            return 'Please complete the payment process';
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        // Small delay to ensure screen is fully mounted
        const timer = setTimeout(() => {
            initiatePayment();
        }, 800);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            clearTimeout(timer);
        };
    }, []);

    const initiatePayment = async () => {
        if (paymentInitiated) {
            console.log('Payment already initiated');
            return;
        }

        setPaymentInitiated(true);

        try {
            if (amount === 0) {
                // Free booking, skip Razorpay
                toast.success('Booking confirmed successfully!');
                handlePaymentSuccess();
                return;
            }

            // Open Razorpay payment modal
            openRazorpay();
        } catch (error) {
            console.error('Payment initiation error:', error);
            toast.error('Failed to initiate payment');
            handlePaymentFailure();
        }
    };

    const openRazorpay = () => {
        if (!orderState) {
            toast.error('Payment order not found');
            handlePaymentFailure();
            return;
        }

        if (!RazorPayKey) {
            console.error('Razorpay API key is missing');
            toast.error('Payment configuration error');
            handlePaymentFailure();
            return;
        }

        const options = {
            key: RazorPayKey,
            amount: Math.round(orderState.amount * 100),
            currency: orderState.currency || 'INR',
            name: 'Players Turf',
            description: 'Players Turf Payment',
            image: images?.Logo,
            order_id: orderState.order_id,
            handler: (response) => {
                console.log('Payment successful:', response);
                if (response?.razorpay_payment_id && response?.razorpay_order_id) {
                    handlePaymentSuccess();
                } else {
                    handlePaymentFailure();
                }
            },
            prefill: {
                name: `${orderState.Userdetails?.first_name || ''} ${orderState.Userdetails?.last_name || ''}`,
                email: orderState.Userdetails?.email || '',
                contact: orderState.Userdetails?.phone || '',
            },
            readonly: {
                email: true,
                contact: true,
                name: true,
            },
            theme: {
                color: '#FE0201',
            },
        };

        try {
            const razorpayInstance = new Razorpay(options);
            razorpayInstance.open();
        } catch (error) {
            console.error('Razorpay failed to initialize', error);
            toast.error('Failed to open payment gateway');
            handlePaymentFailure();
        }
    };

    const handlePaymentSuccess = async () => {
        try {
            const universalPayload = {
                booking_id: Number(bookingId),
                amount: Number(amount),
                payment_status: 'Paid',
            };

            // Call the appropriate payment API based on payment type
            let response;
            if (paymentType === 'partial') {
                response = await partialBookingCourtPayment(universalPayload);
            } else if (paymentType === 'split') {
                response = await splitPaymentCourtPayment(universalPayload);
            } else {
                response = await normalCourtPayment(universalPayload);
            }

            if (response?.status === 'success') {
                toast.success('Payment successful! Booking confirmed.');

                // Navigate to success screen
                navigate('/booking-success', {
                    state: {
                        bookingId: bookingId,
                        bookingData: bookingData,
                        successData: successData,
                    },
                    replace: true,
                });
            } else {
                toast.error(response?.message || 'Payment verification failed');
                handlePaymentFailure();
            }
        } catch (error) {
            console.error('Payment success handler error:', error);
            toast.error('Failed to confirm booking');
            handlePaymentFailure();
        }
    };

    const handlePaymentFailure = () => {
        // Navigate to failure screen
        navigate('/booking-failed', {
            state: {
                bookingId: bookingId,
                bookingData: bookingData,
            },
            replace: true,
        });
    };

    return (
        <div className="min-h-screen bg-white flex items-center justify-center">
            <div className="flex flex-col items-center justify-center px-6 max-w-md">
                <Timer
                    size={80}
                    color="#FE0201"
                    className="animate-pulse"
                />

                <h1 className="text-2xl font-bold text-black mt-6 text-center">
                    Processing Payment
                </h1>

                <p className="text-base font-medium text-gray-600 mt-3 text-center">
                    Please wait while we redirect you to the payment gateway...
                </p>

                <div className="mt-8">
                    <Loader2 className="animate-spin h-12 w-12 text-red-500" />
                </div>

                <div className="mt-8 bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                    <p className="text-sm font-semibold text-yellow-800 text-center">
                        ⚠️ Please do not press back or close the browser during payment
                    </p>
                </div>

                <div className="mt-4 bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <p className="text-xs font-medium text-blue-800 text-center">
                        Your slot is temporarily reserved and will be confirmed after successful payment
                    </p>
                </div>
            </div>
        </div>
    );
};

export default PaymentProcessing;
